import { memoizedGroupBySelector } from '@travelnest/entities';
import { useDataLoad, usePrevious } from '@travelnest/merlin-hooks';
import { IEvent } from '@travelnest/typewriter';
import React from 'react';
import { useStore } from 'react-redux';
import { IPricingDefault, IPricingOverride, IPricingType, ISmartPrice } from '../../../data/entities/pricing';
import { actions as entityActions } from '../../../data/store';
import { fromFloyd } from '../../../entities/pricing/helpers';
import { IThunkDispatch } from '../../../store/middleware';
import { IState } from '../../../store/reducer';
import { ICalendarEvent, ICalendarPrice } from '../../components/calendar/types';
import { CurrencyCodes, currencySymbolForCurrency, SupportedCurrencies } from '../../helpers/currency_helpers';
import { buildEventData, dateStringToDate } from './calendar-helpers';

/**
 * Retrieve a customer's property list (filtering out inactive properties), and a loading state.
 * @param customerId {string} - The customer's ID to fetch properties for.
 * @param shouldLoad {boolean} - If this is set to false, the hook will not load data.
 */
export const useCustomersProperties = (customerId: string, shouldLoad?: boolean) => {
	const load = shouldLoad ?? true;

	const { getState, dispatch }: { getState: () => IState, dispatch: IThunkDispatch } = useStore();
	const properties = (memoizedGroupBySelector('owner_id', getState().data.properties.byId)[customerId] || [])
		.filter((p) => p.is_active)
		.sort((a, b) => a.name.localeCompare(b.name));
	const loading = useDataLoad(load, () => dispatch(entityActions.property.getByOwnerId(customerId)));

	return { properties, loading };
};

/**
 * Retrieve a set of events for a property, mapping them to the format for the calendar to consume.
 * @param propertyId {string | number | undefined} - The property to fetch data for.
 */
export const useEvents = (propertyId: string | number | undefined) => {
	const { dispatch, getState }: { dispatch: IThunkDispatch, getState: () => IState } = useStore();
	const events = propertyId ? getState().data.availability.byPropertyId[propertyId] : undefined;

	// Initially set loading if we have a property, but no events data
	const [dataLoading, setDataLoading] = React.useState<boolean>(events === undefined && !!propertyId);

	const previousPropertyId = usePrevious(propertyId);

	React.useLayoutEffect(() => {
		const getPropertyEvents = async (prop: string | number) => {
			setDataLoading(true);
			await dispatch(entityActions.availability.getByProperty(propertyId!));
			setDataLoading(false);
		};
		if (!!propertyId && propertyId !== previousPropertyId) {
			console.debug(`Fetching events for ${propertyId}`);
			getPropertyEvents(propertyId);
		}

	}, [propertyId, previousPropertyId]);

	const mappedEvents: ICalendarEvent[] = (events || []).map((e: IEvent) => {
		const [status, text, data] = buildEventData(e);
		return {
			// TODO: the typing of IEvent isn't right - it is actually a string, not a date
			start: dateStringToDate(e.start_on as unknown as string),
			end: dateStringToDate(e.end_on as unknown as string),
			text,
			id: e.id,
			status,
			data
		};
	});

	return {
		loading: dataLoading || (propertyId !== previousPropertyId),
		events: mappedEvents
	};
};

/**
 * Retrieve smart pricing for the property
 * @param propertyId the property's ID
 */

export const mapPricing = (
	pricing: ISmartPrice[] | undefined,
	defaults: IPricingDefault | undefined,
	overrides: IPricingOverride[]
): ICalendarPrice[] => {
	return (pricing || []).map((ap) => {
		const currencySymbol = currencySymbolForCurrency(SupportedCurrencies[defaults?.currency || SupportedCurrencies.GBP]);
		const currentDaysOverride = (overrides || []).find((ov) => ov.effective_date === ap.date);
		const smartOrMinPrice = defaults ? Math.max(ap.price, defaults.min_price) : ap.price;
		let date = new Date(ap.date);
		date.setHours(0);
		// creating a date with an ISmartPrice entity date (ISO format, eg '2023-01-01') in regions that have a timezone that is
		// behind UTC (eg San Francisco is UTC -7) was resulting in the date being created the day before we intended, knocking
		// smart prices/overrides rendered on calendar out of sync by 1 day. If the browser is using a timezone behind UTC, we add one day
		// to bring it in line with the data in the pricing DB. Timezones ahead of UTC are not affected.
		date = date.getTimezoneOffset() > 0 ? new Date(date.setDate(date.getDate() + 1)) : date;
		return {
			...ap,
			date,
			price: `${currencySymbol}${fromFloyd(smartOrMinPrice)}`,
			...(currentDaysOverride?.price && { override: `${currencySymbol}${fromFloyd(currentDaysOverride.price)}` })
		};
	});
};

export const useSmartPricing = (propertyId: number | string) => {
	const { dispatch, getState }: { dispatch: IThunkDispatch, getState: () => IState } = useStore();
	const pricingType: IPricingType = propertyId ? getState().data.pricingType.byPropertyId[propertyId] : undefined;
	const defaults: IPricingDefault | undefined = propertyId ? getState().data.pricingDefaults.byPropertyId[propertyId] : undefined;
	const overrides: IPricingOverride[] = propertyId ? getState().data.pricingOverrides.byPropertyId[propertyId] : undefined;

	let pricing: ISmartPrice[] | undefined;
	pricing = pricingType?.type === 'automatic' && propertyId ? getState().data.pricingAutomaticPrices.byPropertyId[propertyId] : undefined;

	// we only want to display 365 days of pricing on the calendar
	if (pricing) pricing = pricing.slice(0, 365);

	// Initially set loading if we have a property, but no smart pricing data
	const [dataLoading, setDataLoading] = React.useState<boolean>(pricing === undefined && !!propertyId);

	const previousPropertyId = usePrevious(propertyId);

	React.useLayoutEffect(() => {
		const getSmartPricing = async () => {
			setDataLoading(true);
			await dispatch(entityActions.pricingAutomaticPrices.getByPropertyId(Number(propertyId)));
			await dispatch(entityActions.pricingDefaults.getByPropertyId(Number(propertyId)));
			await dispatch(entityActions.pricingType.getByPropertyId(Number(propertyId)));
			await dispatch(entityActions.pricingOverrides.getByPropertyId(Number(propertyId)));
			setDataLoading(false);
		};
		if (!!propertyId && propertyId !== previousPropertyId) {
			getSmartPricing();
		}

	}, [propertyId, previousPropertyId]);

	// Calendar component only cares about rendering a string for price, hence the formatting being done here before
	// being passed as a prop later on

	const mappedPricing = mapPricing(pricing, defaults, overrides);

	return {
		loading: dataLoading || (propertyId !== previousPropertyId),
		pricing: mappedPricing,
		pricingType,
		currency: CurrencyCodes[defaults?.currency || 'GBP']
	};
};
